import React, { useCallback, useState } from 'react';
import { View } from 'react-native';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';

import { Button, ButtonGroup, ButtonContainer } from 'app/src/elements/buttons';
import DataState from 'app/src/components/DataState';
import Input from 'app/src/elements/inputs';
import { Text } from 'app/src/styles';

import ScheduleLineInput from './ScheduleLineInput';
import { DAYS_OF_WEEK_OPTIONS } from './index';
import { UPDATE_PRE_BID_SCHEDULE } from './queries';

const EditPreBidSchedule = ({ preBidSchedule, setShowModal, refetch }) => {
  const [submitting, setSubmitting] = useState(false);

  const [updatePreBidSchedule] = useMutation(UPDATE_PRE_BID_SCHEDULE, {
    onCompleted: () => {
      refetch();
      setShowModal(false);
    }
  });

  const values = {
    id: preBidSchedule.id,
    name: preBidSchedule.name,
    saleWeekday: preBidSchedule.saleWeekday || '',
  };

  if (preBidSchedule.schedule.length) {
    values.schedule = preBidSchedule.schedule.map(line => ({
      start: line.start,
      defaultStep: line.defaultStep,
      minStep: line.minStep,
      _destroy: false,
    }));
  } else {
    values.schedule = [
      {
        start: '',
        defaultStep: '',
        minStep: '',
        _destroy: false,
      }
    ];
  }

  const {
    control, handleSubmit, watch, setValue, formState: { errors }
  } = useForm({
    defaultValues: values
  });

  const onSubmit = useCallback(input => {
    if ([0, '0'].includes(input.saleWeekday) || input.saleWeekday) {
      input.saleWeekday = parseInt(input.saleWeekday);
    } else {
      input.saleWeekday = null;
    }

    input.schedule = input.schedule.
      toSorted((a, b) => a.start - b.start).
      filter(line => !line._destroy).
      map(line => ({
        start: line.start, minStep: line.minStep, defaultStep: line.defaultStep
      }));

    setSubmitting(true);
    updatePreBidSchedule({ variables: input });
  });

  if (submitting) { return <DataState.Loading />; }

  const scheduleLines = watch('schedule');

  const addRow = () => {
    const index = scheduleLines.length;
    setValue(`schedule.${index}.start`, '');
    setValue(`schedule.${index}.minStep`, '');
    setValue(`schedule.${index}.defaultStep`, '');
    setValue(`schedule.${index}._destroy`, false);
  };

  const removeRow = index => {
    setValue(`schedule.${index}._destroy`, true);
  };

  return (
    <View>
      <Input.Text
        label="Name:"
        name="name"
        control={control}
        errors={errors}
        rules={{ required: true }}
      />
      <Input.Picker
        label="Default for sale weekday (optional):"
        name="saleWeekday"
        options={DAYS_OF_WEEK_OPTIONS}
        control={control}
        errors={errors}
      />

      {scheduleLines.map((line, index) => (
        line._destroy ? null : (
          <ScheduleLineInput
            key={index}
            line={line}
            control={control}
            index={index}
            remove={() => removeRow(index)}
          />
        )
      ))}

      <Button label="Add Line" onPress={addRow} wide />
      <ButtonContainer>
        <ButtonGroup>
          <Button
            label="Update Pre Bid Schedule"
            onPress={handleSubmit(onSubmit)}
            style={{width: 'fit-content'}}
          />
        </ButtonGroup>
      </ButtonContainer>
    </View>
  );
};

export default EditPreBidSchedule;
